/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Divider from '@mui/material/Divider';
import { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { collection, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { db } from '../../../../firebase.js';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDAlert from 'components/MDAlert';

interface Device {
  id: string;
  name: string;
  lastActive: string;
  browser?: string;
  os?: string;
  ip?: string;
  location?: string;
}

function Sessions(): JSX.Element {
  const auth = getAuth();
  const user = auth.currentUser;

  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch active devices
    fetchActiveDevices();
  }, [user]);

  const fetchActiveDevices = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const userDocRef = doc(db, 'users', user.email);
      const userDevicesCollectionRef = collection(userDocRef, 'devices');

      const devicesSnapshot = await getDocs(userDevicesCollectionRef);
      const devicesList: Device[] = [];

      devicesSnapshot.forEach((deviceDoc) => {
        const deviceData = deviceDoc.data() as Omit<Device, 'id'>;
        devicesList.push({
          id: deviceDoc.id,
          //@ts-ignore
          name: deviceData.deviceName || 'Unknown Device',
          lastActive: deviceData.lastActive || 'Unknown',
          browser: deviceData.browser,
          //@ts-ignore
          os: deviceData.deviceType,
          ip: deviceData.ip,
          location: deviceData.location,
        });
      });

      setDevices(devicesList);
    } catch (err) {
      console.error('Error fetching devices:', err);
      setError('Failed to fetch active devices');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDevice = async (deviceId: string) => {
    if (!user) return;

    try {
      const deviceDocRef = doc(db, 'users', user.email, 'devices', deviceId);
      await deleteDoc(deviceDocRef);
      setDevices(devices.filter((device) => device.id !== deviceId));
    } catch (err) {
      console.error('Error deleting device:', err);
      setError('Failed to delete device');
    }
  };

  const actionButtonStyles = {
    '& .material-icons-round': {
      transform: `translateX(0)`,
      transition: 'all 200ms cubic-bezier(0.34,1.61,0.7,1.3)',
    },
    'cursor': 'pointer',

    '&:hover .material-icons-round, &:focus .material-icons-round': {
      transform: `translateX(4px)`,
    },
  };

  const getDeviceIcon = (device: Device) => {
    const os = device.os?.toLowerCase() || '';

    if (os.includes('iphone') || os.includes('ios')) {
      return 'phone_iphone';
    } else if (os.includes('android')) {
      return 'smartphone';
    } else {
      return 'desktop_windows';
    }
  };

  return (
    <Card id="sessions">
      <MDBox p={3} lineHeight={1}>
        <MDBox mb={1}>
          <MDTypography variant="h5">Active Devices</MDTypography>
        </MDBox>
        <MDTypography variant="button" color="text" fontWeight="regular">
          This is a list of devices that have logged into your account. Remove
          those that you do not recognize.
        </MDTypography>
      </MDBox>

      {error && (
        <MDBox px={3} mb={2}>
          <MDAlert color="error">{error}</MDAlert>
        </MDBox>
      )}

      <MDBox pb={3} px={3} sx={{ overflow: 'auto' }}>
        {loading ? (
          <MDTypography variant="button" color="text">
            Loading devices...
          </MDTypography>
        ) : devices.length > 0 ? (
          devices.map((device, index) => (
            <MDBox key={device.id}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width={{ xs: 'max-content', sm: '100%' }}
              >
                <MDBox display="flex" alignItems="center">
                  <MDBox
                    textAlign="center"
                    color="text"
                    px={{ xs: 0, md: 1.5 }}
                    opacity={0.6}
                  >
                    <Icon>{getDeviceIcon(device)}</Icon>
                  </MDBox>
                  <MDBox height="100%" ml={2} lineHeight={1} mr={2}>
                    <MDTypography
                      display="block"
                      variant="button"
                      fontWeight="regular"
                      color="text"
                    >
                      {device.name} {device.ip && `(${device.ip})`}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {/* Last active: {device.lastActive} */}
                      OS : {device.os}
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox display="flex" alignItems="center">
                  <MDBadge
                    variant="contained"
                    size="xs"
                    badgeContent="active"
                    color="success"
                    container
                  />
                  {device.location && (
                    <MDBox mx={2} lineHeight={1}>
                      <MDTypography
                        variant="button"
                        color="secondary"
                        fontWeight="regular"
                      >
                        {device.location}
                      </MDTypography>
                    </MDBox>
                  )}
                  <MDTypography
                    component="a"
                    // href="#"
                    variant="button"
                    color="error"
                    fontWeight="regular"
                    sx={actionButtonStyles}
                    onClick={() => handleDeleteDevice(device.id)}
                  >
                    Remove
                  </MDTypography>
                </MDBox>
              </MDBox>
              {index < devices.length - 1 && <Divider />}
            </MDBox>
          ))
        ) : (
          <MDTypography variant="button" color="text">
            No active devices found
          </MDTypography>
        )}
      </MDBox>
    </Card>
  );
}

export default Sessions;
